import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Users, RefreshCw, ChevronLeft, ChevronRight, Eye, Pencil,
  Plus, Trash2, Search, X, Save, ScanEye, GripVertical, Globe, Database,
  Mail, Copy, Check, Ban, ListFilter, Send, ScrollText
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { adminManagementApi, preferencesApi } from '../../services/api';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import SearchSelect from '../../components/common/SearchSelect';
import StuiEmailModal from '../../components/common/StuiEmailModal';
import { matchesSearch, parseSearchQuery, SEARCH_HINT } from '../../utils/listSearch';
import { BROADCAST_EMAIL_TEMPLATES } from '../../config/emailTemplates';
import RevisionPickerModal from './RevisionPickerModal';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp, formatTimestampDate } from '../../utils/dateTime';
import { usePermissionMap, KeyUses } from './permissionMap';
import UserAccessModal from './UserAccessModal';

const escapeHtml = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// TipTap stores list items as <li><p>..</p></li>, a template as <li>..</li>; match either.
const HIGHLIGHT_SLOT = /<li>(?:<p>)?\[Highlight\](?:<\/p>)?<\/li>/;

// What search reads, labelled as the user sees it. The labels double as field: prefixes and feed
// the Advanced Search builder. Database and Role come from the user's database role rows, so
// database:GF is "has a role on GF". Active and SysAdmin read Yes / No rather than
// Active / Inactive, because search matches by substring and "active" is inside "inactive".
const SEARCH_FIELD_LABELS = [
  { key: 'displayName', label: 'Name' }, { key: 'email', label: 'Email' },
  { key: 'organizationName', label: 'Organization' }, { key: 'databases', label: 'Database' },
  { key: 'roles', label: 'Role' }, { key: 'activeText', label: 'Active' },
  { key: 'sysAdminText', label: 'SysAdmin' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

const ASSIGNABLE_ROLES = ['Guest', 'User', 'PowerUser', 'AppAdmin'];

const EMPTY_PENDING = {
  roleChanges: {}, addRoles: [], deleteRoleIds: [],
  overrideChanges: {}, addOverrides: [], deleteOverrideIds: [],
  globalChanges: {}, addGlobals: [], deleteGlobalIds: [],
};

const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
// Records a changed value against its saved one; changing it back clears the entry.
const withChange = (map, id, value, saved) => {
  const next = { ...map };
  if (value === saved) delete next[id]; else next[id] = value;
  return next;
};

// Ack popup copy for each kind of global change. A global row outranks instance rows in both
// directions, so each line says what happens in every database, not just this one.
const GLOBAL_CHANGE_COPY = {
  'add-Grant':    { label: 'Add Grant',      effect: 'Allowed in every database. Instance denies will not block it.' },
  'add-Deny':     { label: 'Add Deny',       effect: 'Removed in every database. Instance grants cannot restore it.' },
  'to-Grant':     { label: 'Deny → Grant',   effect: 'Allowed in every database. Instance denies will not block it.' },
  'to-Deny':      { label: 'Grant → Deny',   effect: 'Removed in every database. Instance grants cannot restore it.' },
  'remove-Deny':  { label: 'Remove Deny',    effect: 'Access is RESTORED everywhere the role or an instance grant allows it.' },
  'remove-Grant': { label: 'Remove Grant',   effect: 'Access falls back to role and instance rules in every database.' },
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [listCollapsed, setListCollapsed] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());

  // Instance roles. selectedDbRole is a saved role row, or a staged one carrying a tempId.
  const [addingRole, setAddingRole] = useState(null);
  const [selectedDbRole, setSelectedDbRole] = useState(null);
  const [selectedOverrideKey, setSelectedOverrideKey] = useState(null);
  const [selectedGlobalKey, setSelectedGlobalKey] = useState(null);
  // What the key being granted opens, from the permission map (shown under the grant grids).
  const { map: permissionMap, error: permissionMapError } = usePermissionMap();

  // Every role / override / global change made in edit mode is staged here and written by one
  // transactional Save; Cancel drops it. Nothing in the grids writes on its own.
  const [pending, setPending] = useState(EMPTY_PENDING);
  const tempSeq = useRef(0);
  const nextTempKey = () => `new-${++tempSeq.current}`;
  const [showGlobalAck, setShowGlobalAck] = useState(false);
  const [globalAckChecked, setGlobalAckChecked] = useState(false);

  // Permission overrides
  const [allPermissions, setAllPermissions] = useState([]);
  const [resolvedPerms, setResolvedPerms] = useState(null);
  const [showEffective, setShowEffective] = useState(false);

  // Copy from user
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [revisionPick, setRevisionPick] = useState(null);   // { setSubject, setBodyHtml } while open
  const [copySource, setCopySource] = useState(null);
  const [copyDbScope, setCopyDbScope] = useState('all');
  const [copySaving, setCopySaving] = useState(false);

  // Instance permission add
  const [addingPerm, setAddingPerm] = useState(false);
  const [newPermDefId, setNewPermDefId] = useState('');
  const [newPermAccess, setNewPermAccess] = useState('Grant');

  // Global permissions
  const [showGlobalPerms, setShowGlobalPerms] = useState(false);
  const [globalPerms, setGlobalPerms] = useState([]);
  const [addingGlobalPerm, setAddingGlobalPerm] = useState(false);
  const [newGlobalPermDefId, setNewGlobalPermDefId] = useState('');
  const [newGlobalPermAccess, setNewGlobalPermAccess] = useState('Grant');

  // New user modal
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', organizationId: '', defaultDatabaseId: '', defaultRole: 'User' });
  const [newUserSaving, setNewUserSaving] = useState(false);

  // Invitation state
  const [invitation, setInvitation] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  // Action menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { notify } = useNotifications();
  const { impersonate, users: authUsers, currentUser } = useAuth();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, summaryRes, dbRes, orgRes, permRes] = await Promise.all([
        adminManagementApi.getUsers(),
        adminManagementApi.getSummary(),
        adminManagementApi.getDatabases(),
        adminManagementApi.getOrganizations(),
        adminManagementApi.getPermissions()
      ]);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
      setDatabases(dbRes.data);
      setOrganizations(orgRes.data);
      setAllPermissions(permRes.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadInvitation = async (userId) => {
    try {
      const res = await adminManagementApi.getInvitation(userId);
      setInvitation(res.data);
      setInviteLink(res.data.inviteUrl || null);
    } catch {
      setInvitation(null);
      setInviteLink(null);
    }
  };

  // preserveDatabaseId: re-select the instance role on that database after the refresh. Keyed
  // by database rather than role id so a role created by the Save it follows is found too.
  // Returns the re-selected role, or null.
  const loadUserDetail = async (userId, preserveDatabaseId = null) => {
    try {
      const res = await adminManagementApi.getUser(userId);
      setUserDetail(res.data);
      const keep = preserveDatabaseId != null
        ? (res.data.databaseRoles?.find(dr => dr.databaseId === preserveDatabaseId) || null)
        : null;
      setSelectedDbRole(keep);
      if (!keep) setResolvedPerms(null);
      setEditing(null);
      setDirtyFields(new Set());
      setPending(EMPTY_PENDING);
      return keep;
    } catch (err) {
      console.error('Failed to load user detail:', err);
      return null;
    }
  };

  const loadResolvedPerms = async (userId, databaseId) => {
    try {
      const res = await adminManagementApi.resolvePermissions(userId, databaseId);
      setResolvedPerms(res.data);
    } catch (err) {
      console.error('Failed to resolve permissions:', err);
    }
  };

  const loadGlobalPerms = async (userId) => {
    try {
      const res = await adminManagementApi.getGlobalPermissions(userId);
      setGlobalPerms(res.data);
    } catch (err) {
      console.error('Failed to load global permissions:', err);
    }
  };

  // ---- Staged rows: saved rows with pending changes applied, plus pending adds ----

  const roleRows = [
    ...(userDetail?.databaseRoles || []).map(dr => {
      const deletes = (dr.overrides || []).filter(o => pending.deleteOverrideIds.includes(o.id)).length;
      const adds = pending.addOverrides.filter(a => a.userRoleId === dr.id).length;
      return {
        ...dr,
        savedRole: dr.role,
        role: pending.roleChanges[dr.id] ?? dr.role,
        pendingDelete: pending.deleteRoleIds.includes(dr.id),
        overrideCount: (dr.overrides?.length || 0) - deletes + adds,
      };
    }),
    ...pending.addRoles.map(r => ({
      ...r,
      pendingAdd: true,
      overrideCount: pending.addOverrides.filter(a => a.roleTempId === r.tempId).length,
    })),
  ];
  const isSelectedRole = (r) => !!selectedDbRole && (r.tempId ? selectedDbRole.tempId === r.tempId : selectedDbRole.id === r.id);
  const selectedRoleRow = roleRows.find(isSelectedRole) || null;
  const selectedRoleIsNew = !!selectedDbRole?.tempId;
  const selectedRoleDeleted = !!selectedRoleRow?.pendingDelete;

  // Databases still free for a new role: none of the kept roles and none of the staged ones.
  const freeDatabases = databases.filter(d => !roleRows.some(r => r.databaseId === d.id && !r.pendingDelete));

  const overrideRows = !selectedDbRole ? [] : [
    ...(selectedRoleIsNew ? [] : (resolvedPerms?.overrides || [])).map(o => ({
      rowKey: `o${o.id}`,
      id: o.id,
      permissionKey: o.permissionKey,
      savedAccess: o.access,
      access: pending.overrideChanges[o.id] ?? o.access,
      shadowedByGlobal: o.shadowedByGlobal,
      pendingDelete: selectedRoleDeleted || pending.deleteOverrideIds.includes(o.id),
    })),
    ...pending.addOverrides
      .filter(a => (selectedRoleIsNew ? a.roleTempId === selectedDbRole.tempId : a.userRoleId === selectedDbRole.id))
      .map(a => ({
        rowKey: a.key,
        addKey: a.key,
        permissionKey: allPermissions.find(p => p.id === a.permissionDefId)?.permissionKey,
        access: a.accessType,
        pendingAdd: true,
      })),
  ];

  const globalRows = [
    ...globalPerms.map(gp => ({
      rowKey: `g${gp.id}`,
      id: gp.id,
      permissionDefId: gp.permissionDefId,
      displayName: gp.displayName,
      category: gp.category,
      savedAccess: gp.accessType,
      access: pending.globalChanges[gp.id] ?? gp.accessType,
      pendingDelete: pending.deleteGlobalIds.includes(gp.id),
    })),
    ...pending.addGlobals.map(a => {
      const def = allPermissions.find(p => p.id === a.permissionDefId);
      return { rowKey: a.key, addKey: a.key, permissionDefId: a.permissionDefId, displayName: def?.displayName, category: def?.category, access: a.accessType, pendingAdd: true };
    }),
  ];

  const pendingCount = Object.keys(pending.roleChanges).length + pending.addRoles.length + pending.deleteRoleIds.length
    + Object.keys(pending.overrideChanges).length + pending.addOverrides.length + pending.deleteOverrideIds.length
    + Object.keys(pending.globalChanges).length + pending.addGlobals.length + pending.deleteGlobalIds.length;

  const rowClass = (row) => [
    'stui-clickable',
    row.pendingAdd ? 'stui-row-pending-add' : '',
    row.pendingDelete ? 'stui-row-pending-delete' : '',
  ].filter(Boolean).join(' ');

  // Searchable single-select for the add-permission rows; the key is in the label so a key
  // can be typed as well as a display name.
  const renderPermissionPicker = (value, setValue, perms) => {
    const options = perms.map(p => ({ id: p.id, display: `${p.displayName} (${p.category}) — ${p.permissionKey}` }));
    return (
      <SearchSelect
        value={value}
        displayValue={options.find(o => o.id === value)?.display || ''}
        options={options}
        onChange={v => setValue(v ?? '')}
        placeholder="Search permissions..."
        size="xs"
      />
    );
  };

  // In-cell Grant/Deny picker for edit mode. A changed saved row reads dirty, like a field.
  const renderAccessSelect = (row, onChange, title) => (
    <StuiSelect
      className={`stui-field-input stui-field-input-xs stui-fw-semibold ${row.access === 'Deny' ? 'stui-access-deny' : 'stui-access-grant'}${!row.pendingAdd && row.access !== row.savedAccess ? ' dirty' : ''}`}
      value={row.access}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      title={title}
    >
      <option value="Grant">Grant</option>
      <option value="Deny">Deny</option>
    </StuiSelect>
  );

  // ---- Instance roles ----

  const handleAddUserRole = () => {
    // `editing` guard: a leaked add-row must never be able to stage anything (#115)
    if (!addingRole?.DatabaseId || !editing) return;
    const db = databases.find(d => d.id === addingRole.DatabaseId);
    const role = { tempId: nextTempKey(), databaseId: addingRole.DatabaseId, databaseName: db?.displayName || '', role: addingRole.Role };
    setPending(p => ({ ...p, addRoles: [...p.addRoles, role] }));
    setAddingRole(null);
    // Select it straight away so its overrides can be added without another click.
    setSelectedDbRole(role);
    setSelectedOverrideKey(null);
    setResolvedPerms(null);
  };

  const handleRoleChange = (row, value) => {
    if (row.tempId) {
      setPending(p => ({ ...p, addRoles: p.addRoles.map(r => (r.tempId === row.tempId ? { ...r, role: value } : r)) }));
    } else {
      setPending(p => ({ ...p, roleChanges: withChange(p.roleChanges, row.id, value, row.savedRole) }));
    }
  };

  // Trash on the selected role: a staged role is dropped with its staged overrides; a saved one
  // is marked for removal (or un-marked). Removing a role removes its overrides on Save, so any
  // override additions staged against it are dropped now.
  const handleDeleteSelectedRole = () => {
    if (!selectedDbRole || !editing) return;
    if (selectedRoleIsNew) {
      const tempId = selectedDbRole.tempId;
      setPending(p => ({
        ...p,
        addRoles: p.addRoles.filter(r => r.tempId !== tempId),
        addOverrides: p.addOverrides.filter(o => o.roleTempId !== tempId),
      }));
      setSelectedDbRole(null);
      setResolvedPerms(null);
      return;
    }
    const roleId = selectedDbRole.id;
    setPending(p => ({
      ...p,
      deleteRoleIds: toggleIn(p.deleteRoleIds, roleId),
      addOverrides: p.addOverrides.filter(o => o.userRoleId !== roleId),
    }));
    setAddingPerm(false);
  };

  const handleSelectDbRole = (row) => {
    setSelectedDbRole(row.tempId ? pending.addRoles.find(r => r.tempId === row.tempId) : userDetail.databaseRoles.find(dr => dr.id === row.id));
    setSelectedOverrideKey(null);
    setAddingPerm(false);
    if (row.tempId) setResolvedPerms(null);
    else if (selected) loadResolvedPerms(selected.id, row.databaseId);
  };

  // ---- Instance permission overrides ----

  // The picker stays open after each add so several overrides can be staged in a row.
  const handleAddInstancePerm = () => {
    if (!newPermDefId || !selectedDbRole || !editing || selectedRoleDeleted) return;
    const entry = {
      key: nextTempKey(),
      userRoleId: selectedRoleIsNew ? null : selectedDbRole.id,
      roleTempId: selectedRoleIsNew ? selectedDbRole.tempId : null,
      permissionDefId: parseInt(newPermDefId),
      accessType: newPermAccess,
    };
    setPending(p => ({ ...p, addOverrides: [...p.addOverrides, entry] }));
    setNewPermDefId('');
  };

  const handleOverrideAccess = (row, value) => {
    if (row.addKey) {
      setPending(p => ({ ...p, addOverrides: p.addOverrides.map(a => (a.key === row.addKey ? { ...a, accessType: value } : a)) }));
    } else {
      setPending(p => ({ ...p, overrideChanges: withChange(p.overrideChanges, row.id, value, row.savedAccess) }));
    }
  };

  const handleDeleteOverride = () => {
    const row = overrideRows.find(r => r.rowKey === selectedOverrideKey);
    if (!row || !editing || selectedRoleDeleted) return;
    if (row.addKey) {
      setPending(p => ({ ...p, addOverrides: p.addOverrides.filter(a => a.key !== row.addKey) }));
      setSelectedOverrideKey(null);
    } else {
      setPending(p => ({ ...p, deleteOverrideIds: toggleIn(p.deleteOverrideIds, row.id) }));
    }
  };

  // ---- Global permissions ----

  // The picker stays open after each add so several globals can be staged in a row.
  const handleAddGlobalPerm = () => {
    // `editing` guard: a leaked add-row must never be able to stage anything (#115)
    if (!newGlobalPermDefId || !selected || !editing) return;
    const entry = { key: nextTempKey(), permissionDefId: parseInt(newGlobalPermDefId), accessType: newGlobalPermAccess };
    setPending(p => ({ ...p, addGlobals: [...p.addGlobals, entry] }));
    setNewGlobalPermDefId('');
  };

  const handleGlobalAccess = (row, value) => {
    if (row.addKey) {
      setPending(p => ({ ...p, addGlobals: p.addGlobals.map(a => (a.key === row.addKey ? { ...a, accessType: value } : a)) }));
    } else {
      setPending(p => ({ ...p, globalChanges: withChange(p.globalChanges, row.id, value, row.savedAccess) }));
    }
  };

  const handleDeleteGlobalPerm = () => {
    const row = globalRows.find(r => r.rowKey === selectedGlobalKey);
    if (!row || !editing) return;
    if (row.addKey) {
      setPending(p => ({ ...p, addGlobals: p.addGlobals.filter(a => a.key !== row.addKey) }));
      setSelectedGlobalKey(null);
    } else {
      setPending(p => ({ ...p, deleteGlobalIds: toggleIn(p.deleteGlobalIds, row.id) }));
    }
  };

  // Every staged global change, worded for the acknowledgement popup.
  const globalChangeList = [
    ...globalRows.filter(r => r.pendingAdd).map(r => ({ key: r.rowKey, name: r.displayName, kind: `add-${r.access}` })),
    ...globalRows.filter(r => !r.pendingAdd && !r.pendingDelete && r.access !== r.savedAccess).map(r => ({ key: r.rowKey, name: r.displayName, kind: `to-${r.access}` })),
    ...globalRows.filter(r => r.pendingDelete).map(r => ({ key: r.rowKey, name: r.displayName, kind: `remove-${r.savedAccess}` })),
  ];

  // Clears every inline "add a sub-row" draft so none outlives edit mode — see #115.
  const resetAddState = () => {
    setAddingRole(null);
    setAddingPerm(false);
    setNewPermDefId('');
    setNewPermAccess('Grant');
    setAddingGlobalPerm(false);
    setNewGlobalPermDefId('');
    setNewGlobalPermAccess('Grant');
  };

  // Leaves edit mode and drops everything staged. A selected staged role goes with it.
  const clearEditState = () => {
    setEditing(null);
    setDirtyFields(new Set());
    setPending(EMPTY_PENDING);
    setShowGlobalAck(false);
    resetAddState();
    setSelectedOverrideKey(null);
    setSelectedGlobalKey(null);
    if (selectedDbRole?.tempId) { setSelectedDbRole(null); setResolvedPerms(null); }
  };

  const handleSelectUser = (user) => {
    setSelected(user);
    resetAddState();
    setSelectedDbRole(null);
    setResolvedPerms(null);
    setShowCopyModal(false);
    setEditing(null);
    setDirtyFields(new Set());
    setPending(EMPTY_PENDING);
    setSelectedOverrideKey(null);
    setSelectedGlobalKey(null);
    setShowGlobalPerms(false);
    setGlobalPerms([]);
    loadUserDetail(user.id);
    loadGlobalPerms(user.id);
    loadInvitation(user.id);
    setInvitation(null);
    setInviteLink(null);
    setInviteCopied(false);
  };

  // Edit mode
  const startEdit = () => {
    if (!userDetail) return;
    setEditing({
      displayName: userDetail.displayName,
      email: userDetail.email || '',
      isSysAdmin: !!userDetail.isSysAdmin,
      organizationId: userDetail.organizationId,
      isActive: userDetail.isActive
    });
    setDirtyFields(new Set());
    setPending(EMPTY_PENDING);
    resetAddState();
  };

  const cancelEdit = (force = false) => {
    if (!force && (dirtyFields.size > 0 || pendingCount > 0)) {
      notify.confirm('Discard unsaved changes?', clearEditState);
      return;
    }
    clearEditState();
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = field === 'organizationId' ? userDetail.organizationId
      : field === 'isActive' ? userDetail.isActive
      : field === 'isSysAdmin' ? !!userDetail.isSysAdmin
      : field === 'displayName' ? userDetail.displayName
      : userDetail.email || '';
    const newDirty = new Set(dirtyFields);
    if (value !== original) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const buildSecurityBatch = () => {
    let user = null;
    if (dirtyFields.size > 0) {
      user = {};
      if (dirtyFields.has('displayName')) user.displayName = editing.displayName;
      if (dirtyFields.has('email')) user.email = editing.email;
      if (dirtyFields.has('isSysAdmin')) user.isSysAdmin = editing.isSysAdmin;
      if (dirtyFields.has('organizationId')) user.organizationId = editing.organizationId;
      if (dirtyFields.has('isActive')) user.isActive = editing.isActive;
    }
    return {
      user,
      addRoles: pending.addRoles.map(r => ({ tempId: r.tempId, databaseId: r.databaseId, role: r.role })),
      updateRoles: Object.entries(pending.roleChanges)
        .filter(([id]) => !pending.deleteRoleIds.includes(parseInt(id)))
        .map(([id, role]) => ({ id: parseInt(id), role })),
      deleteRoleIds: pending.deleteRoleIds,
      addOverrides: pending.addOverrides.map(({ userRoleId, roleTempId, permissionDefId, accessType }) => ({ userRoleId, roleTempId, permissionDefId, accessType })),
      updateOverrides: Object.entries(pending.overrideChanges)
        .filter(([id]) => !pending.deleteOverrideIds.includes(parseInt(id)))
        .map(([id, accessType]) => ({ id: parseInt(id), accessType })),
      deleteOverrideIds: pending.deleteOverrideIds,
      addGlobals: pending.addGlobals.map(({ permissionDefId, accessType }) => ({ permissionDefId, accessType })),
      updateGlobals: Object.entries(pending.globalChanges)
        .filter(([id]) => !pending.deleteGlobalIds.includes(parseInt(id)))
        .map(([id, accessType]) => ({ id: parseInt(id), accessType })),
      deleteGlobalIds: pending.deleteGlobalIds,
    };
  };

  // A global change outranks every instance permission, so a Save carrying one has to be
  // acknowledged first. One popup lists them all, then commitSave writes the whole batch.
  const handleSave = () => {
    if (!editing || (dirtyFields.size === 0 && pendingCount === 0)) return;
    if (globalChangeList.length > 0) {
      setGlobalAckChecked(false);
      setShowGlobalAck(true);
      return;
    }
    commitSave();
  };

  const commitSave = async () => {
    setShowGlobalAck(false);
    setSaving(true);
    const userId = userDetail.id;
    const keepDatabaseId = selectedDbRole && !selectedRoleDeleted ? selectedDbRole.databaseId : null;
    try {
      await adminManagementApi.saveUserSecurity(userId, buildSecurityBatch());
      clearEditState();
      setSelectedDbRole(null);
      await load();
      // Re-resolve after the refresh: a saved role or global change alters effective permissions.
      const kept = await loadUserDetail(userId, keepDatabaseId);
      await loadGlobalPerms(userId);
      if (kept) await loadResolvedPerms(userId, kept.databaseId);
      notify.success('User security saved');
    } catch (err) {
      // All-or-nothing on the server: edit mode and everything staged stay put for a retry.
      console.error('Save failed:', err);
      const msg = err?.response?.data;
      notify.error(typeof msg === 'string' && msg ? msg : 'Save failed. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = () => {
    if (!userDetail || !isEditing) return;
    const roleCount = userDetail.databaseRoles?.length || 0;
    const overrideCount = userDetail.databaseRoles?.reduce((sum, dr) => sum + (dr.overrides?.length || 0), 0) || 0;
    const msg = `Permanently delete ${userDetail.displayName}?\n\nThis will remove:\n- The user record\n- ${roleCount} role${roleCount !== 1 ? 's' : ''}\n- ${overrideCount} permission${overrideCount !== 1 ? 's' : ''}\n- Global permissions, preferences and report subscriptions\n- Any invitations`;
    notify.confirm(msg, async () => {
      setSaving(true);
      try {
        const displayName = userDetail.displayName;
        await adminManagementApi.deleteUser(userDetail.id);
        setSelected(null);
        setUserDetail(null);
        clearEditState();
        await load();
        notify.success(`${displayName} deleted`);
      } catch (err) {
        console.error('Delete failed:', err);
        notify.error(err.response?.data?.error || 'Failed to delete user');
      } finally {
        setSaving(false);
      }
    }, { onCancel: () => cancelEdit() });
  };

  // Re-select same DB role after userDetail refresh
  useEffect(() => {
    if (selectedDbRole && !selectedDbRole.tempId && userDetail?.databaseRoles) {
      const refreshed = userDetail.databaseRoles.find(dr => dr.id === selectedDbRole.id);
      if (refreshed) setSelectedDbRole(refreshed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail]);

  const handleCopyFrom = async () => {
    if (!copySource || !selected) return;
    setCopySaving(true);
    try {
      if (copyDbScope === 'all') {
        await adminManagementApi.copyUserRoles(copySource.id, selected.id);
      } else {
        const sourceDetail = await adminManagementApi.getUser(copySource.id);
        const sourceDbRole = sourceDetail.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (!sourceDbRole) { notify.warning(`${copySource.displayName} has no role for that database.`); setCopySaving(false); return; }
        let targetDbRole = userDetail.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole) {
          await adminManagementApi.updateUserRole(targetDbRole.id, { UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        } else {
          await adminManagementApi.createUserRole({ UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        }
        const refreshed = await adminManagementApi.getUser(selected.id);
        targetDbRole = refreshed.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole && sourceDbRole.overrides?.length > 0) {
          for (const o of (targetDbRole.overrides || [])) await adminManagementApi.deleteUserPermission(o.id);
          for (const o of sourceDbRole.overrides) await adminManagementApi.createUserPermission({ UserRoleId: targetDbRole.id, PermissionDefId: o.permissionDefId, AccessType: o.accessType });
        }
      }
      setShowCopyModal(false);
      setCopySource(null);
      await load();
      await loadUserDetail(selected.id);
    } catch (err) {
      console.error('Copy failed:', err);
    } finally {
      setCopySaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim()) return;
    setNewUserSaving(true);
    try {
      await adminManagementApi.createUser({
        firstName:         newUser.firstName.trim(),
        lastName:          newUser.lastName.trim(),
        email:             newUser.email.trim(),
        organizationId:    newUser.organizationId ? parseInt(newUser.organizationId) : null,
        defaultDatabaseId: newUser.defaultDatabaseId ? parseInt(newUser.defaultDatabaseId) : null,
        defaultRole:       newUser.defaultRole || null,
      });
      setShowNewUser(false);
      setNewUser({ firstName: '', lastName: '', email: '', organizationId: '', defaultDatabaseId: '', defaultRole: 'User' });
      await load();
      notify.success('User created');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setNewUserSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!selected) return;
    try {
      const res = await adminManagementApi.sendInvite(selected.id);
      setInvitation({ status: 'pending', expiresAt: res.data.expiresAt, sentAt: res.data.sentAt });
      setInviteLink(res.data.inviteUrl);
      if (res.data.emailSent === false) {
        notify.warning('Invitation saved — email delivery failed. Copy the link from the 6-dot menu to send manually.');
      } else {
        notify.success('Invitation sent');
      }
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to send invitation');
    }
  };

  const handleCopyLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    }).catch(() => {
      // Clipboard API can fail (permissions, focus) — surface the link so it can be copied by hand
      notify.warning(`Couldn't access the clipboard. Invite link: ${inviteLink}`, { timeout: 0 });
    });
  };

  const handleSendTestEmail = async () => {
    if (!selected) return;
    setMenuOpen(false);
    try {
      const res = await adminManagementApi.sendTestEmail(selected.id);
      if (res.data.sent === false) {
        notify.error(`Test email failed: ${res.data.error}`);
      } else {
        const bccNote = res.data.bcc ? ` — a copy was BCC'd to you (${res.data.bcc})` : '';
        notify.successAlert(`Test email sent to ${res.data.sentTo || userDetail?.email || ''}${bccNote}`);
      }
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to send test email');
    }
  };

  const handleRevokeClick = () => {
    if (!selected || !userDetail) return;
    setMenuOpen(false);
    const isSelf = selected.id === currentUser?.id;
    const isSysAdmin = userDetail.isSysAdmin;
    const displayName = userDetail.displayName;
    const msg = isSelf && isSysAdmin
      ? 'You are revoking your own SysAdmin account. You will be immediately locked out of DAWN.'
      : isSelf
        ? 'You are revoking your own account. You will be immediately locked out of DAWN.'
        : isSysAdmin
          ? `This will revoke a SysAdmin account. ${displayName} will be locked out immediately.`
          : `This will remove ${displayName}'s access and clear all invitation records. They will need to be re-invited to return.`;
    notify.confirm(msg, async () => {
      try {
        await adminManagementApi.revokeInvite(selected.id);
        setInvitation({ status: 'none' });
        setInviteLink(null);
        setUserDetail(prev => prev ? { ...prev, azureObjectId: null } : prev);
        notify.success(`Access revoked for ${displayName}`);
      } catch (err) {
        notify.error(err.response?.data?.error || 'Failed to revoke access');
      }
    });
  };

  // Search rows are built once per load so listSearch's per-row text cache stays warm; the list
  // still renders the original user objects.
  const searchRows = useMemo(() => users.map(u => ({
    ...u,
    databases: (u.databaseRoles || []).map(r => r.databaseName),
    roles: (u.databaseRoles || []).map(r => r.role),
    activeText: u.isActive ? 'Yes' : 'No',
    sysAdminText: u.isSysAdmin ? 'Yes' : 'No',
  })), [users]);

  const parsedSearch = useMemo(() => parseSearchQuery(searchTerm, SEARCH_FIELDS, SEARCH_FIELD_LABELS), [searchTerm]);
  const filteredUsers = useMemo(
    () => users.filter((u, i) => matchesSearch(searchRows[i], parsedSearch, SEARCH_FIELDS, SEARCH_FIELD_LABELS)),
    [users, searchRows, parsedSearch]
  );

  // Send Email to Users goes to exactly the list on screen (everyone when unfiltered), minus
  // inactive accounts, which cannot sign in to act on it. The server re-checks both.
  const broadcastRecipients = filteredUsers.filter(u => u.isActive);
  const broadcastSkipped = filteredUsers.length - broadcastRecipients.length;
  const broadcastLabel = `${searchTerm.trim() ? '' : 'All '}${broadcastRecipients.length} user${broadcastRecipients.length === 1 ? '' : 's'}`
    + (broadcastSkipped ? ` (${broadcastSkipped} inactive skipped)` : '');

  // A search naming exactly one database puts it in the locked subject prefix:
  // "DAWN Notice (GF): ". The server builds the prefix; this only names the database.
  const broadcastQualifier = useMemo(() => {
    const named = new Set(parsedSearch.groups.flatMap(g => g.clauses
      .filter(c => c.field === 'databases' && !c.negated)
      .flatMap(c => c.values.map(v => v.toLowerCase()))));
    if (named.size !== 1) return '';
    const [needle] = named;
    return users.flatMap(u => (u.databaseRoles || []).map(r => r.databaseName))
      .find(n => n?.toLowerCase() === needle) || '';
  }, [parsedSearch, users]);

  // Release template: the newest ticked version fills [Version] and the ticked entries' change
  // lines replace the [Highlight] bullet (or are appended when it is already gone).
  const applyRevisions = (revs, { setSubject, setBodyHtml }) => {
    if (!revs.length) return;
    setSubject(sub => sub.split('[Version]').join(revs[0].version));
    const items = revs.flatMap(r => r.changes || []).map(c => `<li>${escapeHtml(c)}</li>`).join('');
    if (!items) return;
    setBodyHtml(body => (HIGHLIGHT_SLOT.test(body) ? body.replace(HIGHLIGHT_SLOT, items) : `${body}<ul>${items}</ul>`));
  };

  const isEditing = editing !== null;
  // The key the grant panel explains: the one being picked, else the selected grant row's.
  const keyOfDef = (id) => allPermissions.find(p => String(p.id) === String(id))?.permissionKey;
  const panelKey = (showGlobalPerms
    ? (addingGlobalPerm && newGlobalPermDefId ? keyOfDef(newGlobalPermDefId) : keyOfDef(globalRows.find(r => r.rowKey === selectedGlobalKey)?.permissionDefId))
    : (addingPerm && newPermDefId ? keyOfDef(newPermDefId) : overrideRows.find(r => r.rowKey === selectedOverrideKey)?.permissionKey)) || null;
  const hasDirty = dirtyFields.size > 0 || pendingCount > 0;

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Users size={20} />
          <h1>Admin Users</h1>
          <span className="stui-module-header-meta">
            {summary ? `${summary.activeUsers} active / ${summary.users} total` : ''}
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* CRUD: + or Trash depending on edit state */}
          {isEditing ? (
            <button className="stui-crud-btn stui-delete-btn" onClick={handleDeleteUser} disabled={saving} title="Delete user"><Trash2 size={18} /></button>
          ) : (
            <button className="stui-crud-btn" onClick={() => setShowNewUser(true)} title="Add user"><Plus size={18} /></button>
          )}
          {/* Edit/Cancel toggle */}
          <button
            className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? cancelEdit : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          {/* Save */}
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          {/* Action menu */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                {/* List-level: act on the list (filtered or all users) */}
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}>
                  <ListFilter size={16} /><span>Advanced Search…</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button
                  className={`stui-action-menu-item ${broadcastRecipients.length === 0 ? 'disabled' : ''}`}
                  disabled={broadcastRecipients.length === 0}
                  title={searchTerm.trim() ? 'Email the active users in the filtered list' : 'Email every active user'}
                  onClick={() => { setMenuOpen(false); setShowBroadcast(true); }}
                >
                  <Send size={16} /><span>Email Users…</span>
                </button>
                {selected && (<>
                  <div className="stui-action-menu-divider" />
                  {/* Working as / configuring this user */}
                  <button className={`stui-action-menu-item ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={() => {
                    setMenuOpen(false);
                    const authUser = authUsers.find(u => u.id === selected?.id);
                    if (authUser) impersonate(authUser);
                  }}>
                    <Users size={16} /><span>Impersonate User</span>
                  </button>
                  {/* Copy writes straight away, so it is kept out of edit mode where it would bypass the staged Save */}
                  <button className={`stui-action-menu-item ${!selected || isEditing ? 'disabled' : ''}`} disabled={!selected || isEditing} title={isEditing ? 'Save or cancel your edits first' : undefined} onClick={() => { setMenuOpen(false); setShowCopyModal(true); }}>
                    <Database size={16} /><span>Copy Roles from…</span>
                  </button>
                  <div className="stui-action-menu-divider" />
                  {/* Getting this user signed in */}
                  {/* Invite / resend / copy — only when not yet accepted */}
                  {!userDetail?.azureObjectId && (<>
                    {(!invitation || invitation.status === 'none' || invitation.status === 'expired') && (
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleSendInvite(); }}>
                        <Mail size={16} /><span>{invitation?.status === 'expired' ? 'Resend Invite' : 'Send Invite'}</span>
                      </button>
                    )}
                    {invitation?.status === 'pending' && (<>
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleSendInvite(); }}>
                        <Mail size={16} /><span>Resend Invite</span>
                      </button>
                      <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); handleCopyLink(); }} disabled={!inviteLink}>
                        {inviteCopied ? <Check size={16} /> : <Copy size={16} />}
                        <span>{inviteCopied ? 'Copied!' : 'Copy Invite Link'}</span>
                      </button>
                    </>)}
                  </>)}
                  <button className="stui-action-menu-item" onClick={handleSendTestEmail}>
                    <Mail size={16} /><span>Send Test Email</span>
                  </button>
                  <div className="stui-action-menu-divider-strong" />
                  {/* Destructive */}
                  <button className={`stui-action-menu-item stui-action-menu-item-danger ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={() => {
                    setMenuOpen(false);
                    notify.confirm(
                      `Restore ALL preferences to defaults for ${userDetail?.displayName || selected?.displayName}? This deletes their saved theme, zoom, timeout, and other preference rows.`,
                      async () => {
                        try {
                          const res = await preferencesApi.restore(selected.id);
                          notify.success(`Preferences restored (${res.data?.deleted ?? 0} rows cleared)`);
                        } catch {
                          notify.error('Failed to restore preferences');
                        }
                      },
                      { confirmText: 'Restore Defaults', cancelText: 'Cancel' }
                    );
                  }}>
                    <RefreshCw size={16} /><span>Restore Preferences</span>
                  </button>
                  {/* Revoke — available any time there is something to revoke */}
                  {(userDetail?.azureObjectId || (invitation && invitation.status !== 'none')) && (
                    <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={handleRevokeClick}>
                      <Ban size={16} /><span>Revoke Access</span>
                    </button>
                  )}
                </>)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand list"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse list"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users..." title={SEARCH_HINT} />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filteredUsers.length === 0 ? (
                <div className="stui-list-empty">No users found</div>
              ) : filteredUsers.map(u => (
                <button key={u.id} onClick={() => handleSelectUser(u)} className={`stui-list-item ${selected?.id === u.id ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">{u.displayName}</span>
                  <span className="stui-list-item-secondary">{u.email || 'No email'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="stui-detail-panel">
          {selected && userDetail ? (
            <div className="stui-detail-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* USER INFORMATION */}
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">User Information</div></div>
                <div className="stui-field-group stui-col-8">
                  <label>Display Name</label>
                  <input className={`stui-field-input${isEditing && dirtyFields.has('displayName') ? ' dirty' : ''}`} value={isEditing ? editing.displayName : userDetail.displayName} onChange={isEditing ? e => handleFieldChange('displayName', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Email</label>
                  <input className={`stui-field-input${isEditing && dirtyFields.has('email') ? ' dirty' : ''}`} value={isEditing ? editing.email : (userDetail.email || '')} onChange={isEditing ? e => handleFieldChange('email', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>SysAdmin</label>
                  <label className="stui-checkbox stui-text-error">
                    <input type="checkbox" className="stui-checkbox-input" checked={isEditing ? !!editing.isSysAdmin : !!userDetail.isSysAdmin} onChange={isEditing ? e => handleFieldChange('isSysAdmin', e.target.checked) : undefined} disabled={!isEditing} />
                    {isEditing && dirtyFields.has('isSysAdmin') && 'changed'}
                  </label>
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Active</label>
                  <label className="stui-checkbox stui-text-error">
                    <input type="checkbox" className="stui-checkbox-input" checked={isEditing ? editing.isActive : userDetail.isActive} onChange={isEditing ? e => handleFieldChange('isActive', e.target.checked) : undefined} disabled={!isEditing} />
                    {isEditing && dirtyFields.has('isActive') && 'changed'}
                  </label>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Organization</label>
                  {isEditing ? (
                    <StuiSelect className={`stui-field-input${dirtyFields.has('organizationId') ? ' dirty' : ''}`} value={editing.organizationId || ''} onChange={e => handleFieldChange('organizationId', e.target.value ? parseInt(e.target.value) : null)}>
                      <option value="">-- None --</option>
                      {organizations.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
                    </StuiSelect>
                  ) : (
                    <input className="stui-field-input" value={userDetail.organizationName || ''} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Last Login</label>
                  <input className="stui-field-input" value={userDetail.lastLogin ? formatTimestamp(userDetail.lastLogin) : 'Never'} readOnly />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Created</label>
                  <input className="stui-field-input" value={userDetail.createdAt ? formatTimestampDate(userDetail.createdAt) : ''} readOnly />
                </div>
                <div className="stui-field-group stui-col-14">
                  <label>Azure Object ID</label>
                  <input className="stui-field-input stui-mono stui-text-sm-muted" value={userDetail.azureObjectId || ''} readOnly />
                </div>
                <div className="stui-field-group stui-col-10">
                  <label>Invite Status</label>
                  <div style={{ paddingTop: 'var(--spacing-xs)' }}>
                    {userDetail.azureObjectId ? (
                      <span className="stui-badge stui-badge-accepted">Accepted</span>
                    ) : !invitation || invitation.status === 'none' ? (
                      <span className="stui-badge stui-badge-none">Not Invited</span>
                    ) : invitation.status === 'pending' ? (
                      <span className="stui-badge stui-badge-pending">Invite Sent</span>
                    ) : invitation.status === 'expired' ? (
                      <span className="stui-badge stui-badge-expired">Expired</span>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* INSTANCE ROLES / GLOBAL PERMISSIONS (toggled by globe) */}
              <div className="stui-related-section stui-shrink-0" style={{ height: '200px' }}>
                <div className="stui-detail-section-header stui-row-8">
                  {/* Left: title or inline-add dropdowns */}
                  <div className="stui-row-8 stui-flex-1 stui-min-w-0">
                    {!(isEditing && (addingRole || addingGlobalPerm)) && (
                      <span className="stui-shrink-0">{showGlobalPerms ? `Global Permissions (${globalRows.length})` : `Instance Roles (${roleRows.length})`}</span>
                    )}
                    {isEditing && !showGlobalPerms && addingRole && (
                      <>
                        <StuiSelect className="stui-field-input stui-field-input-xs stui-flex-1" value={addingRole.DatabaseId} onChange={e => setAddingRole({ ...addingRole, DatabaseId: parseInt(e.target.value) })}>
                          <option value={0}>Select instance...</option>
                          {freeDatabases.map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
                        </StuiSelect>
                        <StuiSelect className="stui-field-input stui-field-input-xs" value={addingRole.Role} onChange={e => setAddingRole({ ...addingRole, Role: e.target.value })} style={{ width: '96px' }}>
                          {ASSIGNABLE_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </StuiSelect>
                      </>
                    )}
                    {/* Instance-scoped keys are excluded here for the opposite and more
                        dangerous reason: a global Grant beats an instance Deny, so granting a
                        per-DB key globally silently overrides an explicit denial everywhere. */}
                    {isEditing && showGlobalPerms && addingGlobalPerm && (
                      <>
                        <div className="stui-flex-1 stui-min-w-0">
                          {renderPermissionPicker(newGlobalPermDefId, setNewGlobalPermDefId, allPermissions
                            .filter(p => !globalRows.some(r => r.permissionDefId === p.id && !r.pendingDelete))
                            .filter(p => p.grantScope !== 'Instance'))}
                        </div>
                        <StuiSelect
                          className={`stui-field-input stui-field-input-xs stui-fw-semibold ${newGlobalPermAccess === 'Deny' ? 'stui-access-deny' : 'stui-access-grant'}`}
                          value={newGlobalPermAccess}
                          onChange={e => setNewGlobalPermAccess(e.target.value)}
                          title="Deny applies across EVERY instance and overrides instance grants"
                          style={{ width: '76px' }}
                        >
                          <option value="Grant">Grant</option>
                          <option value="Deny">Deny</option>
                        </StuiSelect>
                      </>
                    )}
                  </div>
                  {/* Right cluster: Trash | Check+Cancel or Plus — immediately left of Globe.
                      Trash acts on the selected row in every grid; nothing here writes until Save. */}
                  {!showGlobalPerms && !addingRole && isEditing && selectedDbRole && (
                    <button onClick={handleDeleteSelectedRole} title={selectedRoleDeleted ? 'Keep selected role' : 'Remove selected role'} className="stui-related-header-btn danger"><Trash2 size={14} /></button>
                  )}
                  {isEditing && !showGlobalPerms && addingRole && (
                    <>
                      <button onClick={handleAddUserRole} disabled={!addingRole.DatabaseId} title="Add role (written on Save)" className="stui-related-header-btn"><Check size={13} /></button>
                      <button onClick={() => setAddingRole(null)} title="Close" className="stui-related-header-btn"><X size={13} /></button>
                    </>
                  )}
                  {!showGlobalPerms && !addingRole && isEditing && freeDatabases.length > 0 && (
                    <button onClick={() => setAddingRole({ DatabaseId: 0, Role: 'User' })} title="Add role" className="stui-related-header-btn"><Plus size={14} /></button>
                  )}
                  {showGlobalPerms && !addingGlobalPerm && isEditing && selectedGlobalKey && (
                    <button onClick={handleDeleteGlobalPerm} title={globalRows.find(r => r.rowKey === selectedGlobalKey)?.pendingDelete ? 'Keep selected global permission' : 'Remove selected global permission'} className="stui-related-header-btn danger"><Trash2 size={14} /></button>
                  )}
                  {isEditing && showGlobalPerms && addingGlobalPerm && (
                    <>
                      <button onClick={handleAddGlobalPerm} disabled={!newGlobalPermDefId} title="Add global permission (written on Save)" className="stui-related-header-btn"><Check size={13} /></button>
                      <button onClick={() => { setAddingGlobalPerm(false); setNewGlobalPermDefId(''); }} title="Close" className="stui-related-header-btn"><X size={13} /></button>
                    </>
                  )}
                  {showGlobalPerms && !addingGlobalPerm && isEditing && (
                    <button onClick={() => { setAddingGlobalPerm(true); setNewGlobalPermDefId(''); }} title="Add global permission" className="stui-related-header-btn"><Plus size={14} /></button>
                  )}
                  {/* Globe/Database toggle — always far right */}
                  <span
                    onClick={() => { setShowGlobalPerms(!showGlobalPerms); setAddingGlobalPerm(false); setNewGlobalPermDefId(''); setAddingRole(null); }}
                    title={showGlobalPerms ? 'Show instance roles' : 'Show global permissions'}
                    className="stui-shrink-0"
                    style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}
                  >
                    {showGlobalPerms
                      ? <Database size={14} />
                      : <Globe size={14} style={globalPerms.length > 0 ? { color: 'var(--color-warning-text)' } : undefined} />
                    }
                    {!showGlobalPerms && globalPerms.length > 0 && (
                      <span style={{ position: 'absolute', bottom: '-4px', right: '-6px', background: 'var(--color-warning)', color: 'var(--color-on-warning)', borderRadius: '50%', minWidth: '12px', height: '12px', fontSize: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 2px' }}>
                        {globalPerms.length}
                      </span>
                    )}
                  </span>
                </div>
                <div className="stui-related-content">
                  <div className="stui-data-grid-container">
                    <div className="stui-data-grid-scroll-area">
                      {showGlobalPerms ? (
                        <table className="stui-data-grid">
                          <thead><tr><th>Permission</th><th style={{ width: '120px' }}>Category</th><th style={{ width: '100px' }}>Access</th></tr></thead>
                          <tbody>
                            {globalRows.length === 0 ? (
                              <tr><td colSpan={3} className="stui-td-empty">No global permissions assigned</td></tr>
                            ) : globalRows.map(r => (
                              <tr
                                key={r.rowKey}
                                className={rowClass(r)}
                                onClick={() => setSelectedGlobalKey(selectedGlobalKey === r.rowKey ? null : r.rowKey)}
                                style={selectedGlobalKey === r.rowKey ? { backgroundColor: 'var(--color-primary-light)' } : undefined}
                              >
                                <td className="stui-fw-semibold">{r.displayName}</td>
                                <td>{r.category}</td>
                                <td>
                                  {isEditing && !r.pendingDelete ? renderAccessSelect(r, v => handleGlobalAccess(r, v), 'Deny applies across EVERY instance and overrides instance grants') : (
                                    <>
                                      <span className={`stui-fw-semibold ${r.access === 'Deny' ? 'stui-access-deny' : 'stui-access-grant'}`}>{r.access}</span>
                                      {r.access === 'Deny' && (
                                        <>{' '}<span className="stui-fw-semibold stui-access-deny" title="Denied in every instance — overrides instance grants">all</span></>
                                      )}
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <table className="stui-data-grid">
                          <thead><tr><th style={{ width: '120px' }}>Instance</th><th>Description</th><th style={{ width: '120px' }}>Role</th><th style={{ width: '100px' }}>Permissions</th></tr></thead>
                          <tbody>
                            {roleRows.map(r => (
                              <tr key={r.tempId || r.id} className={rowClass(r)} onClick={() => handleSelectDbRole(r)} style={isSelectedRole(r) ? { backgroundColor: 'var(--color-primary-light)' } : undefined}>
                                <td>{r.databaseName}</td>
                                <td>{databases.find(d => d.id === r.databaseId)?.description || ''}</td>
                                <td>
                                  {isEditing && !r.pendingDelete ? (
                                    <StuiSelect
                                      className={`stui-field-input stui-field-input-xs stui-fw-semibold${!r.pendingAdd && r.role !== r.savedRole ? ' dirty' : ''}`}
                                      value={r.role}
                                      onChange={e => handleRoleChange(r, e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                    >
                                      {ASSIGNABLE_ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                                    </StuiSelect>
                                  ) : <span className="stui-fw-semibold">{r.role}</span>}
                                </td>
                                <td>{r.overrideCount}</td>
                              </tr>
                            ))}
                            {roleRows.length === 0 && (
                              <tr><td colSpan={4} className="stui-td-empty">No roles assigned</td></tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* INSTANCE PERMISSIONS */}
              {!showGlobalPerms && <div className="stui-related-section stui-flex-1 stui-mt-md" style={{ minHeight: '150px', display: 'flex', flexDirection: 'column' }}>
                <div className="stui-detail-section-header stui-related-header stui-row-8" style={{ opacity: selectedDbRole ? 1 : 0.4 }}>
                  {/* Left: title or permission dropdown */}
                  <div className="stui-flex-1 stui-min-w-0">
                    {!(isEditing && addingPerm) && (
                      <span>Instance Permissions{selectedRoleRow ? ` — ${selectedRoleRow.databaseName} (${selectedRoleRow.role})` : ' (select instance role)'}</span>
                    )}
                    {/* Global-scoped keys are excluded: an instance override of one writes a row
                        that RequireGlobalPermission ignores, so it reads as access and grants
                        none. The API rejects it too — this only keeps it out of the picker. */}
                    {isEditing && addingPerm && (
                      <div className="stui-row-8">
                        <div className="stui-flex-1 stui-min-w-0">
                          {renderPermissionPicker(newPermDefId, setNewPermDefId, allPermissions
                            .filter(p => !overrideRows.some(r => r.permissionKey === p.permissionKey && !r.pendingDelete))
                            .filter(p => p.grantScope !== 'Global'))}
                        </div>
                        <StuiSelect
                          className={`stui-field-input stui-field-input-xs stui-fw-semibold stui-shrink-0 ${newPermAccess === 'Deny' ? 'stui-access-deny' : 'stui-access-grant'}`}
                          value={newPermAccess}
                          onChange={e => setNewPermAccess(e.target.value)}
                          title="Deny overrides the role default for this instance"
                          style={{ width: '76px' }}
                        >
                          <option value="Grant">Grant</option>
                          <option value="Deny">Deny</option>
                        </StuiSelect>
                      </div>
                    )}
                  </div>
                  {/* Right cluster: Trash | Check+Cancel or Plus — immediately left of ScanEye */}
                  {!addingPerm && isEditing && selectedOverrideKey && !selectedRoleDeleted && (
                    <button onClick={handleDeleteOverride} title={overrideRows.find(r => r.rowKey === selectedOverrideKey)?.pendingDelete ? 'Keep selected override' : 'Remove selected override'} className="stui-related-header-btn danger"><Trash2 size={14} /></button>
                  )}
                  {isEditing && addingPerm && (
                    <button onClick={handleAddInstancePerm} disabled={!newPermDefId} title="Add override (written on Save)" className="stui-related-header-btn"><Check size={13} /></button>
                  )}
                  {isEditing && addingPerm ? (
                    <button onClick={() => { setAddingPerm(false); setNewPermDefId(''); }} title="Close" className="stui-related-header-btn"><X size={13} /></button>
                  ) : (
                    isEditing && selectedDbRole && !selectedRoleDeleted && (
                      <button onClick={() => { setAddingPerm(true); setNewPermDefId(''); }} title="Add permission override" className="stui-related-header-btn"><Plus size={14} /></button>
                    )
                  )}
                  {/* ScanEye — far right for a saved role; a staged role has nothing to resolve yet */}
                  {selectedDbRole && !selectedRoleIsNew && (
                    <button onClick={() => setShowEffective(true)} disabled={!resolvedPerms} title="Effective permissions: keys held and what they can do" className="stui-related-header-btn"><ScanEye size={16} /></button>
                  )}
                </div>
                <div className="stui-related-content">
                  {selectedDbRole && (
                    <div className="stui-data-grid-container">
                      <div className="stui-data-grid-scroll-area">
                        <table className="stui-data-grid">
                          <thead><tr><th>Permission</th><th>Category</th><th style={{ width: '160px' }}>Access</th></tr></thead>
                          <tbody>
                            {/* Saved rows come from the override ROWS, not the reconciled verdict:
                                a global Grant/Deny outranks an override in `permissions`, which
                                would otherwise make an existing override vanish from this grid. */}
                            {!selectedRoleIsNew && !resolvedPerms ? (
                              <tr><td colSpan={3} className="stui-td-empty">Loading...</td></tr>
                            ) : overrideRows.length === 0 ? (
                              <tr><td colSpan={3} className="stui-td-empty">No overrides — using {selectedRoleRow?.role} role defaults</td></tr>
                            ) : overrideRows.map(r => {
                              const def = allPermissions.find(ap => ap.permissionKey === r.permissionKey);
                              return (
                                <tr
                                  key={r.rowKey}
                                  className={rowClass(r)}
                                  onClick={() => setSelectedOverrideKey(selectedOverrideKey === r.rowKey ? null : r.rowKey)}
                                  style={selectedOverrideKey === r.rowKey ? { backgroundColor: 'var(--color-primary-light)' } : undefined}
                                >
                                  <td className="stui-fw-semibold">{def?.displayName || r.permissionKey}</td>
                                  <td>{def?.category || ''}</td>
                                  <td>
                                    <div className="stui-row-8">
                                      {isEditing && !r.pendingDelete
                                        ? renderAccessSelect(r, v => handleOverrideAccess(r, v), 'Deny overrides the role default for this instance')
                                        : <span className={`stui-fw-semibold ${r.access === 'Grant' ? 'stui-access-grant' : 'stui-access-deny'}`}>{r.access}</span>}
                                      {r.shadowedByGlobal && (
                                        <span title="A global permission outranks this override — see effective permissions" className="stui-access-warning stui-text-xs stui-shrink-0">overridden</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>}
              {panelKey && (permissionMap
                ? <KeyUses entry={permissionMap.keys.find(k => k.key === panelKey)} name={panelKey} />
                : <p className="stui-hint stui-mt-md">{permissionMapError || 'Reading the permission map...'}</p>)}
            </div>
          ) : (
            <div className="stui-detail-empty">
              <Eye size={48} />
              <p>Select a user to view details</p>
              <p className="stui-hint">Manage roles, permissions, and organization assignment</p>
            </div>
          )}
        </div>
      </div>

      {/* EFFECTIVE PERMISSIONS: keys held and what they can do */}
      {showEffective && selectedDbRole && resolvedPerms && (
        <UserAccessModal userId={selected?.id} userName={userDetail?.displayName} databaseId={selectedDbRole.databaseId}
          databaseName={selectedDbRole.databaseName} resolvedPerms={resolvedPerms} allPermissions={allPermissions}
          unsavedCount={isEditing && hasDirty ? pendingCount + dirtyFields.size : 0} onClose={() => setShowEffective(false)} />
      )}

      {/* NEW USER MODAL */}
      {showNewUser && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>New User</h3>
              <button className="stui-modal-close" onClick={() => setShowNewUser(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid">
                <div className="stui-field-group stui-col-12">
                  <label>First Name <span className="stui-required">*</span></label>
                  <input className="stui-field-input" value={newUser.firstName} onChange={e => setNewUser(p => ({ ...p, firstName: e.target.value }))} placeholder="First" autoFocus />
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Last Name <span className="stui-required">*</span></label>
                  <input className="stui-field-input" value={newUser.lastName} onChange={e => setNewUser(p => ({ ...p, lastName: e.target.value }))} placeholder="Last" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Display Name (auto)</label>
                  <input className="stui-field-input stui-text-muted" value={newUser.lastName && newUser.firstName ? `${newUser.lastName.trim()}, ${newUser.firstName.trim()}` : ''} readOnly style={{ fontStyle: newUser.lastName || newUser.firstName ? 'normal' : 'italic' }} placeholder="Last, First (generated)" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Email Address <span className="stui-required">*</span></label>
                  <input className="stui-field-input" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@example.com" type="email" />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Organization</label>
                  <StuiSelect className="stui-field-input" value={newUser.organizationId} onChange={e => setNewUser(p => ({ ...p, organizationId: e.target.value }))}>
                    <option value="">-- None --</option>
                    {organizations.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-16">
                  <label>Default Database</label>
                  <StuiSelect className="stui-field-input" value={newUser.defaultDatabaseId} onChange={e => setNewUser(p => ({ ...p, defaultDatabaseId: e.target.value }))}>
                    <option value="">-- None --</option>
                    {databases.map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Default Role</label>
                  <StuiSelect className="stui-field-input" value={newUser.defaultRole} onChange={e => setNewUser(p => ({ ...p, defaultRole: e.target.value }))} disabled={!newUser.defaultDatabaseId}>
                    <option value="Guest">Guest</option>
                    <option value="User">User</option>
                    <option value="PowerUser">PowerUser</option>
                    <option value="AppAdmin">AppAdmin</option>
                  </StuiSelect>
                </div>
              </div>
              <p className="stui-hint">
                After creating the user, select them and use <strong>Send Invite</strong> to generate an invitation link.
              </p>
            </div>
            <div className="stui-modal-footer">
              <button className="btn stui-btn-secondary" onClick={() => setShowNewUser(false)}>Cancel</button>
              <button className="btn stui-btn-primary" onClick={handleCreateUser} disabled={newUserSaving || !newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim()}>
                {newUserSaving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COPY FROM USER MODAL */}
      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={SEARCH_FIELD_LABELS} onApply={setSearchTerm} />

      {showBroadcast && (
        <StuiEmailModal
          title="Send Email to Users"
          audience="users"
          audienceLabel={broadcastLabel}
          subjectQualifier={broadcastQualifier}
          recipientIds={broadcastRecipients.map(u => u.id)}
          recipients={broadcastRecipients.map(u => ({ name: u.displayName, email: u.email }))}
          query={searchTerm.trim() || undefined}
          templates={BROADCAST_EMAIL_TEMPLATES}
          defaultTemplate="notification"
          subjectTools={({ template, setSubject, setBodyHtml }) => template?.revisions && (
            <button className="stui-crud-btn" onClick={() => setRevisionPick({ setSubject, setBodyHtml })} title="Pick revisions from the rev log">
              <ScrollText size={16} />
            </button>
          )}
          allowPreview
          reviewThreshold={5}
          placeholder="Write the message, or pick a template above."
          onClose={() => setShowBroadcast(false)}
        />
      )}

      {revisionPick && (
        <RevisionPickerModal
          onClose={() => setRevisionPick(null)}
          onApply={revs => applyRevisions(revs, revisionPick)}
        />
      )}

      {showCopyModal && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Copy Roles & Permissions</h3>
              <button className="stui-modal-close" onClick={() => { setShowCopyModal(false); setCopySource(null); }}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-modal-hint">Copy roles and permission overrides from another user to <strong>{userDetail?.displayName}</strong>.</p>
              <div className="stui-field-group stui-mb-lg">
                <label>Copy from</label>
                <StuiSelect className="stui-field-input" value={copySource?.id || ''} onChange={e => { const u = users.find(u => u.id === parseInt(e.target.value)); setCopySource(u || null); }}>
                  <option value="">Select a user...</option>
                  {users.filter(u => u.id !== selected?.id).map(u => <option key={u.id} value={u.id}>{u.displayName}{u.email ? ` (${u.email})` : ''}</option>)}
                </StuiSelect>
              </div>
              <div className="stui-field-group stui-mb-lg">
                <label>Scope</label>
                <StuiSelect className="stui-field-input" value={copyDbScope} onChange={e => setCopyDbScope(e.target.value)}>
                  <option value="all">All instances (full mirror)</option>
                  {databases.map(d => <option key={d.id} value={d.id}>{d.displayName} only</option>)}
                </StuiSelect>
              </div>
              {copySource && (
                <div className="stui-inline-notice">
                  {copyDbScope === 'all'
                    ? <span>This will replace ALL roles and permission overrides on <strong>{userDetail?.displayName}</strong> with those from <strong>{copySource.displayName}</strong>.</span>
                    : <span>This will copy the role and permissions for <strong>{databases.find(d => d.id === parseInt(copyDbScope))?.displayName}</strong> from <strong>{copySource.displayName}</strong>.</span>
                  }
                </div>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="btn stui-btn-secondary" onClick={() => { setShowCopyModal(false); setCopySource(null); }}>Cancel</button>
              <button className="btn stui-btn-primary" onClick={handleCopyFrom} disabled={!copySource || copySaving}>{copySaving ? 'Copying...' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* GLOBAL PERMISSION ACKNOWLEDGEMENT — gates any Save that carries a global change */}
      {showGlobalAck && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Global Permission Change — {userDetail?.displayName}</h3>
              <button className="stui-modal-close" onClick={() => setShowGlobalAck(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-modal-hint">
                You are changing <strong>global</strong> permissions for <strong>{userDetail?.displayName}</strong>.
                A global permission applies in <strong>every database</strong> and WINS over any instance permission, in both directions.
              </p>
              <div className="stui-data-grid-container stui-mb-lg">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid">
                    <thead><tr><th>Permission</th><th style={{ width: '120px' }}>Change</th><th>Effect</th></tr></thead>
                    <tbody>
                      {globalChangeList.map(c => (
                        <tr key={c.key}>
                          <td className="stui-fw-semibold">{c.name}</td>
                          <td className={`stui-fw-semibold ${c.kind.startsWith('remove') ? '' : c.kind.endsWith('Deny') ? 'stui-access-deny' : 'stui-access-grant'}`}>{GLOBAL_CHANGE_COPY[c.kind]?.label}</td>
                          <td>{GLOBAL_CHANGE_COPY[c.kind]?.effect}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <label className="stui-checkbox">
                <input type="checkbox" className="stui-checkbox-input stui-checkbox-input-danger" checked={globalAckChecked} onChange={e => setGlobalAckChecked(e.target.checked)} autoFocus />
                I understand these changes apply in every database and override instance permissions.
              </label>
            </div>
            <div className="stui-modal-footer">
              <button className="btn stui-btn-secondary" onClick={() => setShowGlobalAck(false)}>Back to Editing</button>
              <button className="btn stui-btn-danger" onClick={commitSave} disabled={!globalAckChecked || saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsersPage;
